// apps/office/src/components/LabourModal.jsx
import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import pb from '../lib/pocketbaseClient';
import { X, Upload, Eye } from 'lucide-react';

export default function LabourModal({ open, onClose, labour, vendors, sites, onSave }) {
  const [formData, setFormData] = useState({
    vendor: '',
    site: '',
    name: '',
    aadhar: '',
    address: '',
    skill: 'unskilled',
    status: 'active',
    dailyRate: '',
  });

  const [photo, setPhoto] = useState(null);
  const [aadharPhoto, setAadharPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [aadharPreview, setAadharPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Reset & populate form when modal opens
  useEffect(() => {
    if (open) {
      if (labour) {
        setFormData({
          vendor: labour.vendor || '',
          site: labour.site || '',
          name: labour.name || '',
          aadhar: labour.aadhar || '',
          address: labour.address || '',
          skill: labour.skill || 'unskilled',
          status: labour.status || 'active',
          dailyRate: labour.dailyRate || '',
        });
        if (labour.photo) {
          setPhotoPreview(pb.files.getUrl(labour, labour.photo));
        } else {
          setPhotoPreview(null);
        }
        if (labour.aadharPhoto) {
          setAadharPreview(pb.files.getUrl(labour, labour.aadharPhoto));
        } else {
          setAadharPreview(null);
        }
      } else {
        setFormData({
          vendor: '',
          site: '',
          name: '',
          aadhar: '',
          address: '',
          skill: 'unskilled',
          status: 'active',
          dailyRate: '',
        });
        setPhotoPreview(null);
        setAadharPreview(null);
      }
      setPhoto(null);
      setAadharPhoto(null);
      setError('');
      setLoading(false);
      setIsSubmitting(false);
    }
  }, [open, labour]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e, setter, previewSetter) => {
    const file = e.target.files[0] || null;
    setter(file);
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        previewSetter(reader.result);
      };
      reader.readAsDataURL(file);
    } else {
      previewSetter(null);
    }
  };

  const validateForm = () => {
    if (!formData.vendor) {
      setError('Please select a vendor');
      return false;
    }
    if (!formData.site) {
      setError('Please select a site');
      return false;
    }
    if (!formData.name || formData.name.trim() === '') {
      setError('Please enter labour name');
      return false;
    }
    if (!formData.aadhar || formData.aadhar.trim() === '') {
      setError('Please enter Aadhar number');
      return false;
    }
    if (formData.aadhar && !/^\d{12}$/.test(formData.aadhar.replace(/\s/g, ''))) {
      setError('Aadhar number should be 12 digits');
      return false;
    }
    if (formData.dailyRate && parseFloat(formData.dailyRate) < 0) {
      setError('Daily rate cannot be negative');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return;

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setIsSubmitting(true);

    try {
      const data = {
        vendor: formData.vendor,
        site: formData.site,
        name: formData.name.trim(),
        aadhar: formData.aadhar.trim(),
        address: formData.address?.trim() || '',
        skill: formData.skill,
        status: formData.status,
        dailyRate: parseFloat(formData.dailyRate) || 0,
        created_by: pb.authStore.model?.id,
      };

      let saved;
      if (labour) {
        saved = await pb.collection('labour').update(labour.id, data);
      } else {
        saved = await pb.collection('labour').create(data);
      }

      // Upload profile photo if changed
      if (photo) {
        const fd = new FormData();
        fd.append('photo', photo);
        await pb.collection('labour').update(saved.id, fd);
      }

      // Upload Aadhar photo if changed
      if (aadharPhoto) {
        const fd = new FormData();
        fd.append('aadharPhoto', aadharPhoto);
        await pb.collection('labour').update(saved.id, fd);
      }

      if (onSave) {
        await onSave();
      }

      onClose();
    } catch (err) {
      console.error('Error saving labour:', err);
      let errorMessage = 'Failed to save labour. Please try again.';
      if (err.data?.data) {
        const fieldErrors = Object.entries(err.data.data)
          .map(([field, error]) => `${field}: ${error.message}`)
          .join('; ');
        errorMessage = `Validation error: ${fieldErrors}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      vendor: '',
      site: '',
      name: '',
      aadhar: '',
      address: '',
      skill: 'unskilled',
      status: 'active',
      dailyRate: '',
    });
    setPhoto(null);
    setAadharPhoto(null);
    setPhotoPreview(null);
    setAadharPreview(null);
    setError('');
    setLoading(false);
    setIsSubmitting(false);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-slate-800">
            {labour ? '?? Edit Labour' : '? New Labour'}
          </h2>
          <button onClick={handleClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="bg-red-50 text-red-700 px-4 py-2 rounded-lg text-sm mb-4 border border-red-200">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Vendor *
              </label>
              <select
                name="vendor"
                value={formData.vendor}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                required
                disabled={loading}
              >
                <option value="">Select Vendor</option>
                {vendors && vendors.map(v => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Site *
              </label>
              <select
                name="site"
                value={formData.site}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                required
                disabled={loading}
              >
                <option value="">Select Site</option>
                {sites && sites.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Skill *
              </label>
              <select
                name="skill"
                value={formData.skill}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                required
                disabled={loading}
              >
                <option value="skilled">Skilled</option>
                <option value="unskilled">Unskilled</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full px-3 py-2 border rounded-lg bg-white focus:ring-2 focus:ring-amber-400 focus:outline-none"
                disabled={loading}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Full Name *
            </label>
            <Input
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter labour's full name"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Aadhar Number *
            </label>
            <Input
              name="aadhar"
              value={formData.aadhar}
              onChange={handleChange}
              required
              disabled={loading}
              placeholder="Enter 12-digit Aadhar number"
              maxLength="12"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Address
            </label>
            <Input
              name="address"
              value={formData.address}
              onChange={handleChange}
              disabled={loading}
              placeholder="Enter labour's address"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Daily Rate (Rs.)
            </label>
            <Input
              name="dailyRate"
              type="number"
              step="0.01"
              value={formData.dailyRate}
              onChange={handleChange}
              disabled={loading}
              placeholder="Enter daily wage"
            />
            <p className="text-xs text-slate-400 mt-1">Used for salary calculation</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Profile Photo
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, setPhoto, setPhotoPreview)}
                  disabled={loading}
                  className="flex-1"
                />
                {photoPreview && (
                  <img
                    src={photoPreview}
                    alt="Profile"
                    className="w-12 h-12 rounded-full object-cover border"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                Aadhar Card Photo
              </label>
              <div className="flex items-center gap-3">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, setAadharPhoto, setAadharPreview)}
                  disabled={loading}
                  className="flex-1"
                />
                {aadharPreview && (
                  <img
                    src={aadharPreview}
                    alt="Aadhar Card"
                    className="w-12 h-12 object-cover border rounded"
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-amber-500 hover:bg-amber-600 text-white"
              disabled={loading}
            >
              {loading ? 'Saving...' : (labour ? 'Update' : 'Create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}